/**
 * pushNavigation — pure decision for where a tapped push notification sends
 * the user. Extracted from push.ts so it can be unit-tested without OneSignal
 * or a DOM (the routing rule is subtle and has regressed twice).
 *
 * Three outcomes:
 *   • soft — MainApp is already mounted and the tab is one the in-app
 *     switch-tab event fully handles (no entity to restore). No reload.
 *   • restore — MainApp is mounted (or about to be, on a cold-start '/' landing)
 *     and the tab carries an entity (duelId / trybeId / program / eventTab).
 *     MainApp re-applies the params in place via the `trybeup:apply-deeplink`
 *     event + a pending-deeplink stash. No reload → no cold-start login flash.
 *   • hard — an off-MainApp route (MainApp isn't mounted, so a real navigation
 *     is required and its mount-time ?tab= parser restores the entity), or an
 *     admin-surface tab that has to leave /app for /admin.
 */

// Tabs the in-app `trybeup:switch-tab` event fully handles. These carry NO
// entity beyond the tab name, so a bare event is enough. challenges/trybe are
// deliberately absent: they need duelId/trybeId/eventTab from the URL parser.
export const SOFT_SWITCH_TABS = [
  'myday', 'habits', 'workout', 'nutrition', 'journal', 'coach', 'progress', 'vision',
] as const

// challenges/trybe usually carry an entity (duelId / trybeId / eventTab) that
// only the ?tab= URL parser can restore, so they hard-nav. BUT a BARE
// challenges/trybe push — no entity params, e.g. the evening challenge nudge
// "/app?tab=challenges" — has nothing to restore, and hard-nav there triggers a
// full WebView reload that flashes the LOGIN screen during the cold-start auth
// race (founder report 2026-07-18: a "Trial by Fire" nudge opened the sign-in
// page). MainApp already accepts a bare challenges/trybe via the switch-tab
// event AND the pending-tab stash, so those can soft-switch (no reload, no
// login flash). Only bare ones — anything with an entity still hard-navs.
const ENTITYLESS_SOFT_TABS = new Set(['challenges', 'trybe'])

// Keys in additionalData that are NOT restorable entities — a push carrying
// only these is "bare" and safe to soft-switch. Anything else (duelId, trybeId,
// eventTab, …) means the URL parser is needed → hard-nav.
const NON_ENTITY_KEYS = new Set(['tab', 'action', 'beat', 'variant'])

// Tabs that live in the Admin dashboard (/admin), NOT the main app (/app).
// A push deep-linking to one of these — today only the founder-only Concierge
// nudge (backend feature_requests.py → /admin?tab=concierge) — must hard-nav to
// /admin, not /app. The backend strips the URL path and forwards only the query
// params, so the tab NAME is our only signal that the destination is the admin
// surface. Left unhandled, `concierge` isn't a real /app tab, so it hit
// MainApp's unknown-tab catch-all and dumped the founder on My Day instead of
// the Concierge inbox (push audit 2026-07-19).
//
// Kept DELIBERATELY tight to admin-ONLY tab names. `duels` is intentionally
// absent even though it's a valid admin tab: it's also the heavily-used /app
// duel alias, so routing it to /admin would break every user-facing duel push.
// Only add a name here once it's (a) actually deep-linked by a push AND (b) not
// also a valid /app destination.
const ADMIN_TABS = new Set(['concierge'])

/** Build a deep-link URL for `base` (/app or /admin), forwarding every
 *  restorable param and dropping analytics-only tags (beat/variant). */
function buildDeepLinkUrl(
  base: string,
  tab: string,
  action: string | null,
  data: Record<string, unknown>,
): string {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(data)) {
    if (k === 'beat' || k === 'variant') continue
    if (typeof v === 'string' || typeof v === 'number') {
      params.set(k, String(v))
    }
  }
  params.set('tab', tab)
  if (action) params.set('action', action)
  return `${base}?${params.toString()}`
}

/** A tab-less push's full route: the backend forwards the URL's path as
 *  `data.path` and its query params as separate keys (push_service.py), so
 *  "/admin/concierge?request=R1" arrives as {path, request}. Put the query
 *  back, or the Concierge push opened the inbox without the request. */
function withPathQuery(path: string, data: Record<string, unknown>): string {
  // A path that already has its own query (an admin announcement's
  // "/app?try=pilot", sent whole as extra data) is complete as it is.
  if (path.includes('?')) return path
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(data)) {
    // Not part of the destination: the path itself, analytics tags, and the
    // push's own kind label.
    if (k === 'path' || k === 'beat' || k === 'variant' || k === 'kind') continue
    if (typeof v === 'string' || typeof v === 'number') params.set(k, String(v))
  }
  const q = params.toString()
  return q ? `${path}?${q}` : path
}

function carriesRestorableEntity(data: Record<string, unknown>): boolean {
  return Object.keys(data).some(
    (k) => !NON_ENTITY_KEYS.has(k) && data[k] != null && data[k] !== '',
  )
}

export interface PushNavInput {
  /** The `tab` param from the push. Absent for notifications whose destination
   *  is a full page route (e.g. /messages, /profile/{id}) rather than an /app
   *  sub-tab — those route via `data.path` instead. */
  tab: string | undefined
  action: string | null
  /** The push's additionalData — every query param the backend put on the
   *  deep-link URL (tab, duelId, trybeId, eventTab, …), a `path` for bare-route
   *  destinations, plus analytics tags. */
  data: Record<string, unknown>
  pathname: string
}

export type PushNav =
  | { kind: 'soft'; tab: string; fireAction: boolean }
  | { kind: 'restore'; url: string }
  | { kind: 'hard'; url: string }
  | { kind: 'none' }

export function resolvePushNavigation({ tab, action, data, pathname }: PushNavInput): PushNav {
  const onAppRoute = pathname === '/app' || pathname.startsWith('/app/')
  const onMainApp = pathname === '/' || onAppRoute

  // No `tab` on the push. Two kinds of notification land here:
  //   • a full-route destination — "new message" (/messages) or "new follower"
  //     (/profile/{id}) — whose page isn't an /app sub-tab. The backend forwards
  //     that route as `data.path` (see push_service.py); hard-nav straight to it.
  //   • a generic broadcast with no destination at all → 'none': there's nowhere
  //     specific to send them, so the tap just opens the app where it is.
  // Before this, `push.ts` bailed on any tab-less push ("if (!tab) return"), so
  // messages and follower taps silently dropped the user on My Day (founder
  // report 2026-07-24: "notifications always just take me to My Day").
  if (!tab) {
    const path = typeof data.path === 'string' ? data.path : ''
    if (path.startsWith('/')) {
      return { kind: 'hard', url: withPathQuery(path, data) }
    }
    return { kind: 'none' }
  }

  // A soft switch carries ONLY the tab name, so it is right only for a push
  // with nothing else to restore. "Markus just trained" is tab=coach with
  // partnerId + workoutId, a payment push is tab=myday with upgrade=true —
  // soft-switching those dropped the very thing the push was about (review
  // 2026-10-17). Anything with an entity param restores (or hard-navs) with
  // its full query, whatever the tab.
  const softableTab =
    ((SOFT_SWITCH_TABS as readonly string[]).includes(tab) || ENTITYLESS_SOFT_TABS.has(tab)) &&
    !carriesRestorableEntity(data)

  if (onMainApp && softableTab) {
    return { kind: 'soft', tab, fireAction: !!action }
  }

  // Admin-surface destinations (e.g. the Concierge nudge → /admin?tab=concierge)
  // live outside MainApp, so they always hard-nav to /admin — never /app. The
  // backend dropped the original path, so the tab name is the only signal; see
  // ADMIN_TABS. Without this the tab hit MainApp's catch-all → My Day.
  if (ADMIN_TABS.has(tab)) {
    return { kind: 'hard', url: buildDeepLinkUrl('/admin', tab, action, data) }
  }

  // Entity-carrying /app tab (a duel/trybe with an id, a challenges program, …)
  // while MainApp is already mounted — which INCLUDES the cold-start '/' landing
  // a notification tap wakes into. These used to hard-nav (a full WebView
  // reload) so MainApp's ?tab= parser could restore the entity; but that reload
  // races the cold-start auth gate and briefly flashes the LOGIN screen (founder
  // report: a duel push opened the sign-in page for a beat). RESTORE instead:
  // MainApp re-applies these exact params IN PLACE via the
  // `trybeup:apply-deeplink` event + a pending-deeplink stash — same
  // destination, same entity, but no reload and therefore no login flash.
  if (onMainApp) {
    return { kind: 'restore', url: buildDeepLinkUrl('/app', tab, action, data) }
  }

  // Off-MainApp (tapped from /profile, /admin, an onboarding route, …): MainApp
  // isn't mounted, so a real navigation is required — its mount-time ?tab=
  // parser then restores the entity. This reload is safe here: the app was
  // already open and authenticated on that route, so the session is warm.
  return { kind: 'hard', url: buildDeepLinkUrl('/app', tab, action, data) }
}
